import { Blossom } from '@/components/Blossom/Blossom'
import { Captions } from '@/components/Captions/Captions'
import { ControlRail, RailButton } from '@/components/ControlRail/ControlRail'
import { ErrorBanner } from '@/components/ErrorBanner/ErrorBanner'
import { BelowOrb, OrbSlot, Stage, StageIconLink } from '@/components/Layout/Layout'
import { Waiting } from '@/components/Waiting/Waiting'
import {
  EndCallIcon,
  GearIcon,
  MicIcon,
  MicOffIcon,
  PauseIcon,
  PersonIcon,
  PlayIcon,
} from '@/components/icons/Icons'
import { useCopy, useLocaleDescriptor } from '@/i18n/useCopy'
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion'
import { useWakeLock } from '@/hooks/useWakeLock'
import { Bloom } from '@/screens/Welcome/Bloom'
import { Welcome } from '@/screens/Welcome/Welcome'
import { useProfileStore } from '@/store/profileStore'
import { useSessionStore } from '@/store/sessionStore'
import { useSettingsStore } from '@/store/settingsStore'
import { ACTIVE_STATES } from '@/voice/machine'
import { useConversation } from './useConversation'
import styles from './Conversation.module.css'

/**
 * The one screen the product actually is.
 *
 * A first run and a return visit are the same surface in different phases,
 * rather than two routes. That is deliberate: the audio context has to be
 * unlocked by the same gesture that starts the conversation, and a navigation
 * between the tap and the first word would break it on iOS. Meeting Namu for
 * the first time is therefore a phase of this screen too, and `src/screens/
 * Welcome` supplies what that phase puts on it.
 *
 * There is deliberately almost nothing on it. No typed input, no running
 * transcript, no word for what the assistant is currently doing. The mark
 * carries the state, and everything the conversation is about happens out
 * loud. What survives is the mark and three controls.
 *
 * While a question is being asked there is exactly one. The rail and the
 * header links are taken off the screen rather than joined by a fourth and
 * fifth control, because somebody who wants to stop answering must never have
 * to work out which of five things means that. They come back the moment the
 * conversation is open, which is when they start being useful.
 *
 * The status is still announced: `LiveRegions` holds a `role="status"` region
 * that says "listening", "thinking", "speaking" for anyone who cannot watch
 * the field move. Removing the visible label is a design choice; removing the
 * information would have been an accessibility failure.
 */
export function ConversationScreen() {
  const copy = useCopy()
  const locale = useLocaleDescriptor()
  const conversation = useConversation()

  // Deliberately no level selectors here. The orb subscribes to those itself,
  // outside React, so a talking user does not re-render this whole screen sixty
  // times a second on a phone that has better things to do.
  const state = useSessionStore((store) => store.state)
  const muted = useSessionStore((store) => store.muted)
  const held = useSessionStore((store) => store.held)
  const error = useSessionStore((store) => store.error)

  const captionsAlwaysOn = useSettingsStore((store) => store.captionsAlwaysOn)
  const reduceMotionSetting = useSettingsStore((store) => store.reduceMotion)
  const systemReducedMotion = usePrefersReducedMotion()
  const reducedMotion = reduceMotionSetting || systemReducedMotion

  // Nobody should have to poke the screen to stop it sleeping mid-sentence.
  useWakeLock(ACTIVE_STATES.has(state))

  /**
   * Captions are off by default and stay off, with one exception that is not
   * negotiable: a language with no synthesised voice has nothing else. Hausa
   * is that language on every browser today, and captioning is what stops the
   * assistant simply going silent in it.
   */
  const captionsNeeded = locale.noVoiceStrategy === 'caption'
  const showCaptions = captionsAlwaysOn || captionsNeeded

  const centered = conversation.phase !== 'live'
  // The welcome and ended moments are one centred stack, so the mark is drawn a
  // little smaller to leave room for what sits under it. Undefined lets both
  // the orb and the ring around it fall back to the same default.
  const orbSize = centered ? 'min(52vw, 14rem)' : undefined

  /*
   * Two windows, and they are different lengths on purpose.
   *
   * `meeting` is the whole of the first conversation, including the stretch
   * after the invitation where the place question is still owed. The bloom is
   * tied to that, so the orb keeps an unfinished ring rather than dropping its
   * petals the moment Namu stops asking, which would claim a completeness the
   * profile does not have.
   *
   * `asking` is only while a question is actually in flight. Everything that
   * takes over the screen is tied to that instead, so the ordinary controls and
   * the ordinary captions come back between questions rather than being held
   * off for the rest of a session that may never reach the last one.
   */
  const meeting = conversation.onboarding.view.active && conversation.phase === 'live'
  const asking = conversation.onboarding.view.asking

  return (
    <Stage
      layout={centered ? 'centered' : 'split'}
      showWordmark={!centered}
      actions={
        asking ? null : (
          <>
            <StageIconLink to="/profile" label={copy.ui.control.profile}>
              <PersonIcon size={20} />
            </StageIconLink>
            <StageIconLink to="/settings" label={copy.ui.control.settings}>
              <GearIcon size={20} />
            </StageIconLink>
          </>
        )
      }
      rail={
        conversation.phase === 'live' && !asking ? (
          <ControlRail>
            {/*
              Hold and mute stay separate. Mute closes the microphone while
              Namu keeps talking; hold suspends the whole exchange. One control
              cannot do both without forcing someone who only wants to stop the
              assistant to give up their own turn as well.
            */}
            <RailButton
              label={copy.ui.control.hold}
              pressed={held}
              onClick={conversation.toggleHold}
              testId="hold-toggle"
            >
              {held ? <PlayIcon size={24} /> : <PauseIcon size={24} />}
            </RailButton>

            {/*
              Ending the conversation takes the centre, in Kola, wearing the
              hang-up glyph everybody already knows. It is the one action here
              that cannot be undone by pressing it again, so it gets the
              position and the weight, and the two reversible toggles flank it.
            */}
            <RailButton
              label={copy.ui.control.end}
              onClick={() => void conversation.end()}
              variant="danger"
              testId="end-session"
            >
              <EndCallIcon size={26} />
            </RailButton>

            {/*
              The accessible name never changes. A toggle whose label flips
              between "mute" and "unmute" cannot be queried for its state, and
              gets announced twice on every press. State lives in aria-pressed.
            */}
            <RailButton
              label={copy.ui.control.mute}
              pressed={muted}
              onClick={conversation.toggleMute}
              testId="mute-toggle"
            >
              {muted ? <MicOffIcon size={24} /> : <MicIcon size={24} />}
            </RailButton>
          </ControlRail>
        ) : null
      }
    >
      <OrbSlot>
        {/* The orb is the progress. See `Bloom`: the petals are a ring laid
            over the mark, and the mark underneath is unchanged. Both boxes get
            the same size, or the ring is drawn on a circle the mark is not on. */}
        <Bloom petals={meeting ? conversation.onboarding.view.petals : null} size={orbSize}>
          <Blossom
            state={conversation.phase === 'welcome' ? 'idle' : state}
            muted={muted}
            held={held}
            reducedMotion={reducedMotion}
            size={orbSize}
          />
        </Bloom>
      </OrbSlot>

      <BelowOrb>
        {conversation.phase === 'welcome' ? <Wake onBegin={conversation.begin} /> : null}

        {conversation.phase === 'live' ? (
          <div className={styles.live}>
            {error ? (
              <div className={styles.panelSlot}>
                <ErrorBanner onRetry={() => void conversation.retryMicrophone()} />
              </div>
            ) : null}

            {asking ? (
              <Welcome
                view={conversation.onboarding.view}
                onSkip={conversation.onboarding.runner.skip}
                onTyped={conversation.onboarding.runner.answerTyped}
              />
            ) : null}

            {/*
              Only ever on screen when a wait has gone past what anybody could
              read as ordinary, and nothing at all before that. It stays
              mounted through a question too: a wait is exactly the moment
              somebody part-way through onboarding most needs telling. What a
              screen reader hears comes from `LiveRegions`, not from here.
            */}
            <Waiting reducedMotion={reducedMotion} />

            {/*
              A question carries its own captions, whatever the captions
              setting, and it carries them because nobody has learned the orb
              yet and because one of those questions is the terms. Once Namu
              stops asking, this is an ordinary conversation and the setting
              decides again. Only one of the two bands is ever on screen: the
              same sentence in two places is not twice the accessibility.
            */}
            {showCaptions && !asking ? <Captions /> : null}
          </div>
        ) : null}

        {conversation.phase === 'ended' ? (
          <div className={styles.ended}>
            {/* The same glass control as the welcome screen: resting and
                resuming are the same moment, so they are the same object. */}
            <span className={styles.beginFrame}>
              <button
                type="button"
                className={styles.begin}
                onClick={() => void conversation.begin()}
                data-testid="begin-button"
              >
                <span className={styles.beginLabel}>{copy.ui.resumeLabel}</span>
              </button>
            </span>
          </div>
        ) : null}
      </BelowOrb>
    </Stage>
  )
}

/**
 * The first thing anyone sees: the mark, the name, and one thing to press.
 *
 * The tagline, the language chips and the line explaining what happens next
 * have all gone. Everything this screen used to say is said out loud instead,
 * either by the greeting or by the bilingual opening.
 * Neither can come before the tap, because no browser will let a page make a
 * sound until somebody has touched it, so the button is what unlocks the voice
 * rather than something the voice has to explain.
 *
 * Named for the moment rather than for the flow behind it, since the first
 * conversation proper now lives in `src/screens/Welcome`. There is no language
 * menu here and there will not be one: the language of the first reply settles
 * it, which is what `src/onboarding/detectLocale.ts` exists to do.
 */
function Wake({ onBegin }: { onBegin(): Promise<void> }) {
  const copy = useCopy()
  const returning = useProfileStore((store) => store.profile.completedAt !== null)

  return (
    <div className={styles.welcome}>
      <h1 className={styles.wordmark}>{copy.ui.appName}</h1>

      {/* The frame carries the bloom the glass refracts; the button is the
          pane itself. Two elements because one cannot sit both behind and in
          front of its own backdrop filter. */}
      <span className={styles.beginFrame}>
        <button
          type="button"
          className={styles.begin}
          onClick={() => void onBegin()}
          data-testid="begin-button"
        >
          <span className={styles.beginLabel}>
            {returning ? copy.ui.resumeLabel : copy.ui.beginLabel}
          </span>
        </button>
      </span>
    </div>
  )
}
